import Phaser from 'phaser';
import { ToasterTruck } from '../sprites/ToasterTruck';
import { Toast } from '../sprites/Toast';
import { Enemy } from '../sprites/Enemy';
import { EnemyProjectile } from '../sprites/EnemyProjectile';
import { Barrier } from '../sprites/Barrier';
import { WaveManager } from '../systems/WaveManager';
import { ScoreManager } from '../systems/ScoreManager';
import { CollisionManager } from '../systems/CollisionManager';
import { PowerUpManager } from '../systems/PowerUpManager';
import { HUD } from '../ui/HUD';
import { GAME_WIDTH, GAME_HEIGHT, PLAYER } from '../config/constants';
import { getDifficultySettings } from '../config/difficultySettings';

export class GameScene extends Phaser.Scene {
  private truck!: ToasterTruck;
  private toastGroup!: Phaser.Physics.Arcade.Group;
  private enemyGroup!: Phaser.Physics.Arcade.Group;
  private enemyProjectileGroup!: Phaser.Physics.Arcade.Group;
  private barrierGroup!: Phaser.Physics.Arcade.Group;
  
  private waveManager!: WaveManager;
  private scoreManager!: ScoreManager;
  private collisionManager!: CollisionManager;
  private powerUpManager!: PowerUpManager;
  private hud!: HUD;
  
  private starField!: Phaser.GameObjects.Graphics;
  private stars: { x: number; y: number; size: number; alpha: number }[] = [];
  
  private lives: number = 10;
  private isGameOver: boolean = false;
  private isPaused: boolean = false;
  private wavesToWin: number = 3;
  private tookDamageThisWave: boolean = false;
  private pauseMenu?: Phaser.GameObjects.Container;
  private confirmDialog?: Phaser.GameObjects.Container;
  private pauseKey?: Phaser.Input.Keyboard.Key;
  private escKey?: Phaser.Input.Keyboard.Key;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    // Get difficulty settings
    const difficultySettings = getDifficultySettings();
    
    // Reset state
    this.lives = difficultySettings.lives;
    this.isGameOver = false;
    this.isPaused = false;
    this.tookDamageThisWave = false;

    // Create animated star field background
    this.createStarField();

    // Create groups
    this.toastGroup = this.physics.add.group({
      classType: Toast,
      runChildUpdate: true,
    });

    this.enemyGroup = this.physics.add.group({
      classType: Enemy,
      runChildUpdate: true,
    });

    this.enemyProjectileGroup = this.physics.add.group({
      classType: EnemyProjectile,
      runChildUpdate: true,
    });

    this.barrierGroup = this.physics.add.group({
      classType: Barrier,
    });

    // Create protective barriers
    this.createBarriers();

    // Create player truck
    this.truck = new ToasterTruck(
      this,
      PLAYER.START_X,
      PLAYER.START_Y,
      this.toastGroup
    );

    // Create managers
    this.scoreManager = new ScoreManager();
    this.waveManager = new WaveManager(this, this.enemyGroup, this.enemyProjectileGroup);
    this.collisionManager = new CollisionManager(this, this.scoreManager, this.waveManager);
    this.powerUpManager = new PowerUpManager(this, this.truck);

    // Setup collisions (including barriers and power-ups)
    this.collisionManager.setPowerUpManager(this.powerUpManager);
    this.collisionManager.setup(
      this.truck,
      this.toastGroup,
      this.enemyGroup,
      this.enemyProjectileGroup,
      this.barrierGroup,
      this.powerUpManager.getGroup()
    );

    // Setup callbacks
    this.collisionManager.setOnPlayerHit(() => this.handlePlayerHit());
    this.scoreManager.setOnScoreChange((score) => this.hud.updateScore(score));
    
    // Spawn power-up on enemy kill
    this.collisionManager.setOnEnemyDestroyed((enemy) => {
      this.powerUpManager.trySpawnPowerUp(enemy.x, enemy.y);
    });

    // Create HUD
    this.hud = new HUD(this);

    // Spawn first wave
    this.waveManager.spawnWave();
    this.showWaveAnnouncement(1);

    // Create boundary visualization
    this.createBoundaries();

    // Setup pause keys
    if (this.input.keyboard) {
      this.pauseKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.P);
      this.escKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    }
  }

  update(time: number, delta: number): void {
    // Check for pause key press (works even when paused)
    if (this.pauseKey && Phaser.Input.Keyboard.JustDown(this.pauseKey)) {
      this.togglePause();
    }
    if (this.escKey && Phaser.Input.Keyboard.JustDown(this.escKey)) {
      this.togglePause();
    }

    if (this.isGameOver || this.isPaused) return;

    // Update truck
    this.truck.update(time, delta);

    // Update wave manager (enemy movement and shooting)
    this.waveManager.update(time, delta);

    // Update power-up manager
    this.powerUpManager.update(time);
    this.hud.updatePowerUps(this.powerUpManager.getActivePowerUps());

    // Update star field animation
    this.updateStarField();

    // Clean up off-screen projectiles
    this.cleanupProjectiles();

    // Check wave completion
    if (this.waveManager.isWaveComplete()) {
      this.handleWaveComplete();
    }

    // Check if enemies reached the bottom (game over condition)
    this.checkEnemyReachedBottom();
  }

  private handlePlayerHit(): void {
    // Check if shield absorbs the hit
    if (this.powerUpManager.onPlayerHit()) {
      // Shield absorbed the damage
      this.cameras.main.shake(100, 0.005);
      return;
    }
    
    this.lives--;
    this.tookDamageThisWave = true;
    this.hud.updateLives(this.lives);

    // Screen shake
    this.cameras.main.shake(200, 0.01);

    // Flash truck red
    this.truck.setTint(0xff0000);
    this.time.delayedCall(100, () => {
      this.truck.clearTint();
    });

    if (this.lives <= 0) {
      this.gameOver(false);
    }
  }

  private handleWaveComplete(): void {
    const currentWave = this.waveManager.getWaveNumber();
    
    // Add wave clear bonus
    this.scoreManager.addWaveBonus(currentWave);
    
    // Add no damage bonus if applicable
    if (!this.tookDamageThisWave) {
      this.scoreManager.addNoDamageBonus();
      this.hud.showMessage('PERFECT WAVE! +200', 1500);
    }

    // Check for victory
    if (currentWave >= this.wavesToWin) {
      this.gameOver(true);
      return;
    }

    // Start next wave
    this.tookDamageThisWave = false;
    this.waveManager.nextWave();
    this.hud.updateWave(this.waveManager.getWaveNumber());
    this.showWaveAnnouncement(this.waveManager.getWaveNumber());
  }

  private checkEnemyReachedBottom(): void {
    const enemies = this.enemyGroup.getChildren() as Enemy[];
    for (const enemy of enemies) {
      if (enemy.active && enemy.y > GAME_HEIGHT - 100) {
        this.gameOver(false);
        break;
      }
    }
  }

  private gameOver(victory: boolean): void {
    this.isGameOver = true;

    // Disable truck
    this.truck.setActive(false);

    // Show game over message
    const message = victory ? 'VICTORY!' : 'GAME OVER';
    this.hud.showMessage(message, 2000);

    // Transition to game over scene
    this.time.delayedCall(2000, () => {
      this.scene.start('GameOverScene', {
        score: this.scoreManager.getScore(),
        victory: victory,
        wave: this.waveManager.getWaveNumber(),
      });
    });
  }

  private togglePause(): void {
    if (this.confirmDialog?.visible) return; // Don't toggle if confirm dialog is showing
    
    this.isPaused = !this.isPaused;
    
    if (this.isPaused) {
      this.physics.pause();
      this.showPauseMenu();
    } else {
      this.physics.resume();
      this.hidePauseMenu();
    }
  }

  private showPauseMenu(): void {
    if (this.pauseMenu) {
      this.pauseMenu.setVisible(true);
      return;
    }

    // Create pause menu container
    this.pauseMenu = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2);

    // Dark overlay
    const overlay = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7);
    
    // Title
    const title = this.add.text(0, -120, 'PAUSED', {
      fontFamily: 'Arial Black',
      fontSize: '48px',
      color: '#ffff00',
    }).setOrigin(0.5);

    // Resume button
    const resumeBtn = this.createMenuButton(0, -30, 'RESUME', () => {
      this.togglePause();
    });

    // Restart button
    const restartBtn = this.createMenuButton(0, 30, 'RESTART', () => {
      this.showConfirmDialog('Restart game?', () => {
        this.cleanupAndExit('GameScene');
      });
    });

    // Quit button
    const quitBtn = this.createMenuButton(0, 90, 'QUIT TO MENU', () => {
      this.showConfirmDialog('Quit to menu?', () => {
        this.cleanupAndExit('HomeScene');
      });
    });

    // Instructions
    const hint = this.add.text(0, 160, 'Press ESC or P to resume', {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#888888',
    }).setOrigin(0.5);

    this.pauseMenu.add([overlay, title, resumeBtn, restartBtn, quitBtn, hint]);
    this.pauseMenu.setDepth(1000);
  }

  private hidePauseMenu(): void {
    if (this.pauseMenu) {
      this.pauseMenu.setVisible(false);
    }
    if (this.confirmDialog) {
      this.confirmDialog.setVisible(false);
    }
  }

  private createMenuButton(x: number, y: number, text: string, onClick: () => void): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add.rectangle(0, 0, 220, 45, 0x333333)
      .setStrokeStyle(2, 0x00ffff);
    
    const label = this.add.text(0, 0, text, {
      fontFamily: 'Arial Black',
      fontSize: '18px',
      color: '#ffffff',
    }).setOrigin(0.5);

    container.add([bg, label]);

    bg.setInteractive({ useHandCursor: true });
    
    bg.on('pointerover', () => {
      bg.setFillStyle(0x444444);
      bg.setStrokeStyle(3, 0xffff00);
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(0x333333);
      bg.setStrokeStyle(2, 0x00ffff);
    });

    bg.on('pointerdown', onClick);

    return container;
  }

  private showConfirmDialog(message: string, onConfirm: () => void): void {
    if (this.confirmDialog) {
      this.confirmDialog.destroy();
    }

    this.confirmDialog = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2);

    // Dialog background
    const bg = this.add.rectangle(0, 0, 300, 180, 0x222222)
      .setStrokeStyle(3, 0xff6b35);

    // Message
    const msg = this.add.text(0, -50, message, {
      fontFamily: 'Arial Black',
      fontSize: '24px',
      color: '#ffffff',
    }).setOrigin(0.5);

    // Yes button
    const yesBtn = this.add.container(-60, 30);
    const yesBg = this.add.rectangle(0, 0, 100, 40, 0x00aa00)
      .setStrokeStyle(2, 0x00ff00);
    const yesText = this.add.text(0, 0, 'YES', {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0.5);
    yesBtn.add([yesBg, yesText]);

    yesBg.setInteractive({ useHandCursor: true });
    yesBg.on('pointerover', () => yesBg.setFillStyle(0x00cc00));
    yesBg.on('pointerout', () => yesBg.setFillStyle(0x00aa00));
    yesBg.on('pointerdown', () => {
      this.confirmDialog?.destroy();
      this.confirmDialog = undefined;
      onConfirm();
    });

    // No button
    const noBtn = this.add.container(60, 30);
    const noBg = this.add.rectangle(0, 0, 100, 40, 0xaa0000)
      .setStrokeStyle(2, 0xff0000);
    const noText = this.add.text(0, 0, 'NO', {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0.5);
    noBtn.add([noBg, noText]);

    noBg.setInteractive({ useHandCursor: true });
    noBg.on('pointerover', () => noBg.setFillStyle(0xcc0000));
    noBg.on('pointerout', () => noBg.setFillStyle(0xaa0000));
    noBg.on('pointerdown', () => {
      this.confirmDialog?.setVisible(false);
    });

    this.confirmDialog.add([bg, msg, yesBtn, noBtn]);
    this.confirmDialog.setDepth(1001);
  }

  private cleanupAndExit(targetScene: string): void {
    // Resume physics
    this.physics.resume();
    this.isPaused = false;
    this.isGameOver = false;

    // Remove keyboard listeners
    if (this.pauseKey) {
      this.pauseKey.destroy();
    }
    if (this.escKey) {
      this.escKey.destroy();
    }
    this.input.keyboard?.removeAllListeners();

    // Clear all tweens
    this.tweens.killAll();

    // Clear all timers
    this.time.removeAllEvents();

    // Destroy UI elements
    if (this.pauseMenu) {
      this.pauseMenu.destroy();
      this.pauseMenu = undefined;
    }
    if (this.confirmDialog) {
      this.confirmDialog.destroy();
      this.confirmDialog = undefined;
    }

    // Stop this scene and start target
    this.scene.stop('GameScene');
    this.scene.start(targetScene);
  }

  private cleanupProjectiles(): void {
    // Clean up off-screen toast
    this.toastGroup.getChildren().forEach((toast) => {
      const t = toast as Toast;
      if (t.y < -20) {
        t.destroy();
      }
    });

    // Clean up off-screen enemy projectiles
    this.enemyProjectileGroup.getChildren().forEach((proj) => {
      const p = proj as EnemyProjectile;
      if (p.y > GAME_HEIGHT + 20) {
        p.destroy();
      }
    });
  }

  private createStarField(): void {
    this.starField = this.add.graphics();
    this.stars = [];
    
    // Generate random stars
    for (let i = 0; i < 100; i++) {
      this.stars.push({
        x: Phaser.Math.Between(0, GAME_WIDTH),
        y: Phaser.Math.Between(0, GAME_HEIGHT),
        size: Phaser.Math.FloatBetween(0.5, 2),
        alpha: Phaser.Math.FloatBetween(0.2, 0.8),
      });
    }
  }

  private updateStarField(): void {
    this.starField.clear();
    
    this.stars.forEach((star) => {
      // Twinkle effect
      star.alpha += Phaser.Math.FloatBetween(-0.02, 0.02);
      star.alpha = Phaser.Math.Clamp(star.alpha, 0.1, 1);
      
      // Draw star
      this.starField.fillStyle(0xffffff, star.alpha);
      this.starField.fillCircle(star.x, star.y, star.size);
    });
  }

  private createBoundaries(): void {
    const graphics = this.add.graphics();
    
    // Draw subtle boundary lines
    graphics.lineStyle(1, 0x00ffff, 0.3);
    
    // Left boundary
    graphics.lineBetween(40, 60, 40, GAME_HEIGHT - 30);
    
    // Right boundary
    graphics.lineBetween(GAME_WIDTH - 40, 60, GAME_WIDTH - 40, GAME_HEIGHT - 30);
    
    // Top boundary (below HUD)
    graphics.lineBetween(40, 60, GAME_WIDTH - 40, 60);
  }

  private showWaveAnnouncement(waveNumber: number): void {
    // Background flash
    const flashBg = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 50, GAME_WIDTH, 100, 0x000000, 0);
    flashBg.setDepth(100);
    
    this.tweens.add({
      targets: flashBg,
      alpha: 0.7,
      duration: 200,
      yoyo: true,
      hold: 1200,
      onComplete: () => flashBg.destroy(),
    });

    // Wave number glow
    const waveGlow = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 70, `WAVE ${waveNumber}`, {
      fontFamily: 'Arial Black',
      fontSize: '56px',
      color: '#ff8800',
    }).setOrigin(0.5).setAlpha(0).setScale(0.5).setDepth(101);

    // Wave number text
    const waveText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 70, `WAVE ${waveNumber}`, {
      fontFamily: 'Arial Black',
      fontSize: '52px',
      color: '#ffff00',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5).setAlpha(0).setScale(0.5).setDepth(102);

    // Animate in
    this.tweens.add({
      targets: [waveText, waveGlow],
      alpha: 1,
      scale: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });

    // Glow pulse
    this.tweens.add({
      targets: waveGlow,
      alpha: { from: 0.4, to: 0.7 },
      scale: { from: 1, to: 1.05 },
      duration: 200,
      yoyo: true,
      repeat: 3,
    });

    // Subtitle
    const subtitle = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, 'GET READY!', {
      fontFamily: 'Arial Black',
      fontSize: '24px',
      color: '#00ffff',
    }).setOrigin(0.5).setAlpha(0).setDepth(102);

    this.tweens.add({
      targets: subtitle,
      alpha: 1,
      duration: 200,
      delay: 200,
    });

    // Animate out
    this.tweens.add({
      targets: [waveText, waveGlow, subtitle],
      alpha: 0,
      scale: 1.3,
      duration: 400,
      delay: 1200,
      onComplete: () => {
        waveText.destroy();
        waveGlow.destroy();
        subtitle.destroy();
      },
    });

    // Side decorations
    const leftLine = this.add.rectangle(0, GAME_HEIGHT / 2 - 50, 0, 4, 0x00ffff, 1).setOrigin(0, 0.5).setDepth(101);
    const rightLine = this.add.rectangle(GAME_WIDTH, GAME_HEIGHT / 2 - 50, 0, 4, 0x00ffff, 1).setOrigin(1, 0.5).setDepth(101);

    this.tweens.add({
      targets: leftLine,
      width: GAME_WIDTH / 2 - 150,
      duration: 300,
      ease: 'Power2',
    });
    this.tweens.add({
      targets: rightLine,
      width: GAME_WIDTH / 2 - 150,
      duration: 300,
      ease: 'Power2',
    });

    this.tweens.add({
      targets: [leftLine, rightLine],
      alpha: 0,
      duration: 300,
      delay: 1200,
      onComplete: () => {
        leftLine.destroy();
        rightLine.destroy();
      },
    });
  }

  private createBarriers(): void {
    const difficultySettings = getDifficultySettings();
    const barrierCount = difficultySettings.barrierCount;
    
    if (barrierCount <= 0) return;
    
    const barrierY = GAME_HEIGHT - 150; // Above the truck
    const totalWidth = GAME_WIDTH - 160; // Leave margins
    const spacing = barrierCount > 1 ? totalWidth / (barrierCount - 1) : 0;
    const startX = barrierCount > 1 ? 80 : GAME_WIDTH / 2;

    for (let i = 0; i < barrierCount; i++) {
      const x = startX + i * spacing;
      const barrier = new Barrier(this, x, barrierY);
      this.barrierGroup.add(barrier);
    }
  }
}
